// Builds the ordered set of design-system Table column elements for the Opportunities list. Extracted from
// the page so every per-column render callback (name badge + navigation, win-probability dot, currency,
// avatars, the status Pill / contact / date-or-add custom columns, and the kebab actions) is assembled in
// one unit-testable place. The page just drops the returned array into <Table>. Column order mirrors
// OPPORTUNITY_COLUMNS; each column's `visible` is driven by the page's getVisibleColumns result, and each
// sortable column's DS `id` equals its OpportunitySortKey so sort <-> session translation is 1:1.
import React from 'react';
import {Table, ColoredIdIcon} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for the DS Align enum (see the custom columns)
import {Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import {OPPORTUNITY_COLUMNS, type OpportunityColumnName} from './opportunityColumns';
import {opportunitySortingFns, type OpportunityTableRow} from './opportunityTableRows';
import {confirmDeleteOpportunity} from './opportunityRowActions';
import {createQuoteTarget} from './opportunityQuoteActions';
import {trackEvent} from '../../tracking/amplitude/TrackingV2';
import {
	targetValueEdit,
	canEditOpportunity,
	canEditLabels,
	TARGET_VALUE_READ_ONLY,
	type TargetValueReadOnly,
} from './opportunityInlineEdit';
import EditableCell from './columns/EditableCell';
import StageColumn from './columns/StageColumn';
import LabelsColumn from './columns/LabelsColumn';
import LabelsEditor from './columns/editors/LabelsEditor';
import SalespersonEditor from './columns/editors/SalespersonEditor';
import ProjectDatesEditor from './columns/editors/ProjectDatesEditor';
import WinProbabilityStageEditor from './columns/editors/WinProbabilityStageEditor';
import TargetValueEditor from './columns/editors/TargetValueEditor';
import type {StageBand} from './opportunityStage';
import LinkColumn from './columns/LinkColumn';
import StatusPillColumn from './columns/StatusPillColumn';
import QuoteStatusColumn from './columns/QuoteStatusColumn';
import QuoteExpiryColumn from './columns/QuoteExpiryColumn';
import ContactColumn from './columns/ContactColumn';
import DateOrAddColumn from './columns/DateOrAddColumn';
import SalespersonColumn from './columns/SalespersonColumn';
import WinProbabilityColumn from './columns/WinProbabilityColumn';
import TargetValueColumn from './columns/TargetValueColumn';
import CustomFieldColumn from './columns/CustomFieldColumn';
import {
	booleanLabelsFrom,
	customFieldColumnId,
	type CustomFieldColumnDef,
} from '../shared/components/custom-field-columns/customFieldColumnModel';
import {CUSTOM_FIELD_DEFAULT_WIDTH_PX, CUSTOM_FIELD_MIN_WIDTH_PX, NATIVE_COLUMN_WIDTHS} from './opportunityTableWidths';

export interface OpportunityColumnsContext {
	formatMessage: (descriptor: {id: string}, values?: Record<string, string>) => string;
	// Whether a column is currently visible (from getVisibleColumns: tab / financial permission / eye-selector).
	isVisible: (name: OpportunityColumnName) => boolean;
	// Navigate to a row's project detail (from the name / stage cells).
	onOpenProject: (row: OpportunityTableRow) => void;
	// Navigate to a row's client page (from the Client cell) — a no-op when the row has no client.
	onOpenClient: (row: OpportunityTableRow) => void;
	// Navigate to an in-app path (from the Create Quote control).
	navigate: (path: string) => void;
	// Refetch the list after a row mutation (add-dates / delete / update win probability).
	onMutationComplete: () => void;
	/** The account's live stage mapping, for the quick actions' stage-naming toasts. */
	bands: StageBand[];
	// The custom fields to show, in turned-on order (visibleCustomFields). [] = none.
	customFields: CustomFieldColumnDef[];
	// For number formatting in custom field cells (react-intl's locale).
	locale: string;
	// The viewer's decimal separator (decimalSeparatorFor), so custom field numbers read the way the inline
	// editor parses them. Omitted = the locale's own.
	decimalSeparator?: ',' | '.';
	// Wraps a custom field cell's content in its inline editor. Omitted = display only.
	customFieldCell?: (content: React.ReactNode, row: OpportunityTableRow, def: CustomFieldColumnDef) => React.ReactNode;
}

const MESSAGE_ID = OPPORTUNITY_COLUMNS.reduce((acc, c) => {
	acc[c.name] = c.messageId;
	return acc;
}, {} as Record<OpportunityColumnName, string>);

export function buildOpportunityColumns(ctx: OpportunityColumnsContext): React.ReactElement[] {
	const {
		formatMessage,
		isVisible,
		onOpenProject,
		onOpenClient,
		navigate,
		onMutationComplete,
		bands,
		customFields,
		locale,
		decimalSeparator,
		customFieldCell,
	} = ctx;
	const header = (name: OpportunityColumnName): string => formatMessage({id: MESSAGE_ID[name]});
	const dataCy = (name: OpportunityColumnName): string => `opportunities-col-${name}`;
	// What a Yes/No custom field column reads, in the viewer's language.
	const booleanLabels = booleanLabelsFrom(formatMessage);

	// The five editable cells (spec 008 §2). Each column supplies its resting value; EditableCell owns
	// the affordance and the PROJECTS_UPDATE gate, and each editor collects a value and closes.
	//
	// Every editor commits through Util.CommitMutation in its own module and then calls
	// onMutationComplete - the page's retry() (D13): a stage change re-buckets a row across the
	// Open/Won/Lost/Unconverted tabs and changes the tab counts, which a Relay-store patch would leave stale.
	const salespersonCell = (content: React.ReactNode, row: OpportunityTableRow) => (
		<EditableCell
			editable={canEditOpportunity(row.node)}
			tooltip={formatMessage({id: 'opportunities.inline_edit.salesperson_tooltip'})}
			dataCy="edit-salesperson"
			editor={({close}) => (
				<SalespersonEditor
					node={row.node}
					onCommit={onMutationComplete}
					close={close}
					unassignedLabel={formatMessage({id: 'opportunities.inline_edit.unassigned'})}
					loadingLabel={formatMessage({id: 'opportunities.inline_edit.salesperson_loading'})}
					failedLabel={formatMessage({id: 'opportunities.inline_edit.salesperson_failed'})}
				/>
			)}
		>
			{content}
		</EditableCell>
	);

	// Labels (spec 010 D2/D3): gated like a custom field - project edit rights, no revenue check. The editor
	// reuses the salesperson "Loading…" copy; its failure copy names labels.
	const labelsCell = (content: React.ReactNode, row: OpportunityTableRow) => (
		<EditableCell
			editable={canEditLabels(row.node)}
			tooltip={formatMessage({id: 'opportunities.inline_edit.labels_tooltip'})}
			dataCy="edit-labels"
			editor={({close}) => (
				<LabelsEditor
					node={row.node}
					onCommit={onMutationComplete}
					close={close}
					title={formatMessage({id: 'common.labels'})}
					updateLabel={formatMessage({id: 'common.update'})}
					cancelLabel={formatMessage({id: 'common.cancel'})}
					loadingLabel={formatMessage({id: 'opportunities.inline_edit.salesperson_loading'})}
					failedLabel={formatMessage({id: 'opportunities.inline_edit.labels_failed'})}
					searchLabel={formatMessage({id: 'opportunities.inline_edit.labels_search'})}
					noCategoryLabel={formatMessage({id: 'settings_labels.no_category'})}
					allAppliedLabel={formatMessage({id: 'opportunities.inline_edit.labels_all_applied'})}
					noMatchLabel={query => formatMessage({id: 'opportunities.inline_edit.labels_no_match'}, {query})}
					removeLabel={label => formatMessage({id: 'opportunities.inline_edit.labels_remove'}, {label})}
				/>
			)}
		>
			{content}
		</EditableCell>
	);

	// One range serves both date cells, so both open the same editor (§2.2). For a row missing a date it
	// is step two of the missing-date popover instead (§2.3).
	const datesEditor = ({close}: {close: () => void}, row: OpportunityTableRow) => (
		<ProjectDatesEditor
			node={row.node}
			onCommit={onMutationComplete}
			close={close}
			datesLabel={formatMessage({id: 'common.dates'})}
			updateLabel={formatMessage({id: 'common.update'})}
			cancelLabel={formatMessage({id: 'common.cancel'})}
			toast={formatMessage({id: 'opportunities.add_project_dates.toast'}, {project: row.name})}
			invertedLabel={formatMessage({id: 'opportunities.inline_edit.dates_inverted'})}
			incompleteLabel={formatMessage({id: 'opportunities.inline_edit.dates_incomplete'})}
		/>
	);
	const datesCell = (content: React.ReactNode, row: OpportunityTableRow) => (
		<EditableCell
			editable={canEditOpportunity(row.node)}
			tooltip={formatMessage({id: 'opportunities.inline_edit.dates_tooltip'})}
			dataCy="edit-dates"
			editor={args => datesEditor(args, row)}
		>
			{content}
		</EditableCell>
	);

	// Win Probability % and Stage share ONE editor (§2.4); bands come from the page (D6).
	// Curried on its data-cy: Win Probability % and Stage are two distinct cells in the same row that
	// happen to open the SAME editor. Baking one hook into both made every row carry two elements with
	// data-cy="edit-win-probability", so anything selecting that cell matched twice and could not say
	// which column it meant.
	const winStageCell = (dataCy: string) => (content: React.ReactNode, row: OpportunityTableRow) =>
		(
			<EditableCell
				editable={canEditOpportunity(row.node)}
				tooltip={formatMessage({id: 'opportunities.inline_edit.win_probability_tooltip'})}
				dataCy={dataCy}
				editor={({close}) => (
					<WinProbabilityStageEditor
						node={row.node}
						bands={bands}
						formatMessage={formatMessage}
						onCommit={onMutationComplete}
						close={close}
						title={formatMessage({id: 'opportunities.inline_edit.win_probability_title'})}
						closeAsLabel={formatMessage({id: 'opportunities.inline_edit.close_as'})}
						wonLabel={formatMessage({id: 'opportunities.stage.won'})}
						lostLabel={formatMessage({id: 'opportunities.stage.lost'})}
						updateLabel={formatMessage({id: 'common.update'})}
						cancelLabel={formatMessage({id: 'common.cancel'})}
					/>
				)}
			>
				{content}
			</EditableCell>
		);

	// §2.5 / D5: editability and the field a save writes are decided per row by the pure predicate. A
	// row with no writable source stays read-only rather than committing a silent no-op.
	const targetValueCell = (content: React.ReactNode, row: OpportunityTableRow) => {
		const edit = targetValueEdit(row.node);
		// A read-only cell says why on hover, and the three causes get three answers: a calculated value
		// that cannot be edited anywhere, a baseline toggle to turn on, or an end date to set. Collapsing
		// the last two sent people to the date picker when the real cause was the baseline.
		// Keyed on the reason union, not string: this map is exactly what has to stay in sync with the
		// reasons, and a string index signature would let a new reason compile with no copy and then
		// throw inside a cell render on formatMessage({id: undefined}).
		const READ_ONLY_COPY: Record<TargetValueReadOnly, string> = {
			[TARGET_VALUE_READ_ONLY.NO_BASELINE]: 'opportunities.inline_edit.target_value_no_baseline',
			[TARGET_VALUE_READ_ONLY.NO_END_DATE]: 'opportunity.target_value.end_date_required',
			[TARGET_VALUE_READ_ONLY.CALCULATED]: 'opportunities.inline_edit.target_value_calculated',
		};
		const readOnlyTooltip = edit.editable ? undefined : formatMessage({id: READ_ONLY_COPY[edit.reason]});
		return (
			<EditableCell
				editable={edit.editable && canEditOpportunity(row.node)}
				readOnlyTooltip={readOnlyTooltip}
				tooltip={formatMessage({id: 'opportunities.inline_edit.target_value_tooltip'})}
				dataCy="edit-target-value"
				editor={({close}) => (
					<TargetValueEditor
						node={row.node}
						onCommit={onMutationComplete}
						close={close}
						label={formatMessage({id: 'opportunities.inline_edit.target_value_title'})}
						updateLabel={formatMessage({id: 'common.update'})}
						cancelLabel={formatMessage({id: 'common.cancel'})}
					/>
				)}
			>
				{content}
			</EditableCell>
		);
	};
	// Every column is sized in px (spec 010 D7). `size` is the column's DEFAULT width - the design-system Table draws
	// the saved width from its columnSizing prop and resets to `size` on a grip double-click - and Table clamps a drag
	// at `minSize`. Defaults and minimums are the design canvas's (D11): size every column for its widest state - a
	// Pill, "Start date missing" - and raise a `min` in NATIVE_COLUMN_WIDTHS if a control overflows it.
	const sizing = (name: OpportunityColumnName) => ({
		size: NATIVE_COLUMN_WIDTHS[name].default,
		minSize: NATIVE_COLUMN_WIDTHS[name].min,
	});
	return [
		<LinkColumn
			key="name"
			id="name"
			accessorKey="name"
			data-cy={dataCy('name')}
			header={header('name')}
			visible={isVisible('name')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.name}
			{...sizing('name')}
			icon={(row: OpportunityTableRow) => <ColoredIdIcon color={row.projectColor}>{row.customId}</ColoredIdIcon>}
			href={(row: OpportunityTableRow) => row.opportunityLinkPath}
			onOpen={onOpenProject}
		/>,
		<LinkColumn
			key="client"
			id="client"
			accessorKey="clientName"
			data-cy={dataCy('client')}
			header={header('client')}
			visible={isVisible('client')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.client}
			{...sizing('client')}
			href={(row: OpportunityTableRow) => (row.node.client?.id ? `/clients/${row.node.client.id}` : undefined)}
			onOpen={onOpenClient}
		/>,
		<ContactColumn
			key="primaryContact"
			id="contact"
			accessorKey="contactName"
			data-cy={dataCy('primaryContact')}
			header={header('primaryContact')}
			visible={isVisible('primaryContact')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.contact}
			{...sizing('primaryContact')}
		/>,
		<TargetValueColumn
			key="targetValue"
			id="targetValue"
			accessorKey="targetValue"
			data-cy={dataCy('targetValue')}
			header={header('targetValue')}
			visible={isVisible('targetValue')}
			rowCurrency={(row: OpportunityTableRow) => row.currency}
			maximumFractionDigits={0}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.targetValue}
			// 002 D13: a blank Target Value is a genuine lowest value (leads ascending, trails descending),
			// which the comparator expresses on its own — unlike Win Probability, which keeps sortUndefined.
			// `false` is explicit rather than omitted: table-core defaults sortUndefined to 1 and resolves
			// blanks in a pre-pass BEFORE sortingFn runs, so an omitted value only works here because
			// TargetValueColumn always writes the key. Stating it keeps the comparator authoritative.
			sortUndefined={false}
			{...sizing('targetValue')}
			cellContent={targetValueCell}
		/>,
		<WinProbabilityColumn
			key="winProbability"
			id="winChance"
			accessorKey="winChanceSort"
			data-cy={dataCy('winProbability')}
			header={header('winProbability')}
			visible={isVisible('winProbability')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.winChance}
			sortUndefined="last"
			{...sizing('winProbability')}
			cellContent={winStageCell('edit-win-probability')}
		/>,
		<StageColumn
			key="stage"
			id="stage"
			accessorKey="stageLabel"
			data-cy={dataCy('stage')}
			header={header('stage')}
			visible={isVisible('stage')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.stage}
			{...sizing('stage')}
			cellContent={winStageCell('edit-stage')}
		/>,
		<LabelsColumn
			key="labels"
			id="labels"
			accessorKey="labelsSortKey"
			data-cy={dataCy('labels')}
			header={header('labels')}
			visible={isVisible('labels')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.labels}
			{...sizing('labels')}
			cellContent={labelsCell}
			addLabel={formatMessage({id: 'opportunities.inline_edit.add_label'})}
			canEdit={(row: OpportunityTableRow) => canEditLabels(row.node)}
		/>,
		<DateOrAddColumn
			key="startDate"
			id="startDate"
			accessorKey="startDate"
			data-cy={dataCy('startDate')}
			header={header('startDate')}
			visible={isVisible('startDate')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.startDate}
			{...sizing('startDate')}
			dateLabel={(row: OpportunityTableRow) => row.startDateLabel}
			cellContent={datesCell}
			editor={datesEditor}
			canEdit={(row: OpportunityTableRow) => canEditOpportunity(row.node)}
			missingLabel={formatMessage({id: 'opportunities.start_date_missing'})}
			missingTitle={formatMessage({id: 'opportunities.date_missing.start.title'})}
			missingBody={formatMessage({id: 'opportunities.date_missing.start.body'})}
			missingActionLabel={formatMessage({id: 'opportunities.date_missing.start.action'})}
			closeLabel={formatMessage({id: 'common.close'})}
			missingDataCy="add-start-date"
		/>,
		<DateOrAddColumn
			key="endDate"
			id="endDate"
			accessorKey="endDate"
			data-cy={dataCy('endDate')}
			header={header('endDate')}
			visible={isVisible('endDate')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.endDate}
			{...sizing('endDate')}
			dateLabel={(row: OpportunityTableRow) => row.endDateLabel}
			cellContent={datesCell}
			editor={datesEditor}
			canEdit={(row: OpportunityTableRow) => canEditOpportunity(row.node)}
			missingLabel={formatMessage({id: 'opportunities.end_date_missing'})}
			missingTitle={formatMessage({id: 'opportunities.date_missing.end.title'})}
			missingBody={formatMessage({id: 'opportunities.date_missing.end.body'})}
			missingActionLabel={formatMessage({id: 'opportunities.date_missing.end.action'})}
			closeLabel={formatMessage({id: 'common.close'})}
			missingDataCy="add-end-date"
		/>,
		<QuoteStatusColumn
			key="quoteStatus"
			id="quoteStatus"
			accessorKey="quoteStatus"
			data-cy={dataCy('quoteStatus')}
			header={header('quoteStatus')}
			visible={isVisible('quoteStatus')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.quoteStatus}
			noQuoteLabel={formatMessage({id: 'opportunities.quote_status.none'})}
			{...sizing('quoteStatus')}
			createQuoteLabel={formatMessage({id: 'baseline.create_quote'})}
			createQuoteTarget={(row: OpportunityTableRow) => createQuoteTarget(row.node)}
			onCreateQuote={(_row, target) => {
				trackEvent('Create Quote Button', 'Clicked', {location: 'Opportunities List', destination: target.destination});
				navigate(target.href);
			}}
		/>,
		<QuoteExpiryColumn
			key="quoteExpiry"
			id="quoteExpiry"
			accessorKey="quoteExpiryLabel"
			data-cy={dataCy('quoteExpiry')}
			header={header('quoteExpiry')}
			visible={isVisible('quoteExpiry')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.quoteExpiry}
			{...sizing('quoteExpiry')}
		/>,
		<StatusPillColumn
			key="status"
			id="status"
			accessorKey="statusState"
			data-cy={dataCy('status')}
			header={header('status')}
			visible={isVisible('status')}
			align={Align.center}
			allowSorting
			sortingFn={opportunitySortingFns.status}
			unconvertedHelp={formatMessage({id: 'opportunities.status.unconverted_help'})}
			unconvertedHelpLabel={formatMessage({id: 'opportunities.status.unconverted_help_label'})}
			{...sizing('status')}
		/>,
		<SalespersonColumn
			key="salesperson"
			id="salesperson"
			accessorKey="salespersonName"
			data-cy={dataCy('salesperson')}
			header={header('salesperson')}
			visible={isVisible('salesperson')}
			align={Align.left}
			allowSorting
			sortingFn={opportunitySortingFns.salesperson}
			{...sizing('salesperson')}
			cellContent={salespersonCell}
			unassignedLabel={formatMessage({id: 'opportunities.inline_edit.unassigned'})}
		/>,
		...customFields.map(def => (
			<CustomFieldColumn
				key={customFieldColumnId(def)}
				id={customFieldColumnId(def)}
				header={def.displayName}
				definition={def}
				locale={locale}
				decimalSeparator={decimalSeparator}
				booleanLabels={booleanLabels}
				visible
				size={CUSTOM_FIELD_DEFAULT_WIDTH_PX}
				minSize={CUSTOM_FIELD_MIN_WIDTH_PX}
				data-cy={`opportunities-col-cf-${def.key}`}
				cellContent={customFieldCell ? (content, row) => customFieldCell(content, row, def) : undefined}
			/>
		)),
		<Table.ActionColumn key="actions" header="">
			{/* §4 / US2: "Update Win Probability & Stage" and the two Set-Win-Probability quick actions moved
			    into the stage popover — the picker for the stage itself, Close as Won / Lost for the two
			    conversions. Delete is all that is left with nowhere else to live. This ships in the same PR
			    as the inline editors, never before them: removing these while inline edit is absent would
			    leave no way to change a stage or close an opportunity from the list at all. */}
			<Table.ActionColumn.Option
				data-cy="action-delete"
				onClick={(row: OpportunityTableRow) => confirmDeleteOpportunity(row.node, onMutationComplete, formatMessage)}
			>
				{formatMessage({id: 'common.delete'})}
			</Table.ActionColumn.Option>
		</Table.ActionColumn>,
	];
}
